<template>
  <v-card class="d-flex flex-column" height="100vh">
    <v-sheet class="flex-grow-1 overflow-auto">
      <v-toolbar color="primary" density="compact" class="px-4">
        <v-icon icon="mdi-clipboard-check-outline" class="mr-2"/>
        <v-toolbar-title class="text-subtitle-1 font-weight-medium">
          {{ checklist?.title || 'Checkliste' }}
          <span v-if="checklist?.asset_id" class="font-weight-regular opacity-90">
            - {{ checklist.asset_id }}
          </span>
        </v-toolbar-title>

        <!-- Bearbeiten-Button (nur Admins/Mods) -->
        <v-btn
            v-if="isAdminOrMod && !isCompleted"
            icon
            variant="text"
            @click="openEditDialog"
        >
          <v-icon icon="mdi-pencil-outline"/>
          <v-tooltip activator="parent" location="bottom">Zuweisung bearbeiten</v-tooltip>
        </v-btn>

        <!-- Mobile: Buttons in Toolbar -->
        <template v-if="useGlobalSetting().isMobile">
          <v-btn
              v-if="canEdit && hasUnsavedChanges"
              icon
              variant="text"
              :loading="saving"
              @click="saveProgress"
          >
            <v-icon icon="mdi-content-save-outline"/>
            <v-tooltip activator="parent" location="bottom">Zwischenspeichern</v-tooltip>
          </v-btn>

          <v-btn
              v-if="canEdit"
              icon
              variant="text"
              :disabled="!allChecked || (hasNio && nioMissingNote) || saving"
              :loading="saving"
              @click="completeChecklist"
          >
            <v-icon icon="mdi-check-all"/>
            <v-tooltip activator="parent" location="bottom">
              {{ hasNio ? 'Mit Mängeln abschließen' : 'Abschließen' }}
            </v-tooltip>
          </v-btn>

          <v-btn icon variant="text" @click="onClose">
            <v-icon icon="mdi-close"/>
            <v-tooltip activator="parent" location="bottom">Schließen</v-tooltip>
          </v-btn>
        </template>
      </v-toolbar>

      <!-- Lock Warning -->
      <v-alert
          v-if="lockedByOther"
          type="warning"
          variant="tonal"
          class="ma-4"
          prominent
      >
        <div class="font-weight-bold">Wird gerade bearbeitet</div>
        <div class="text-body-2">
          Diese Checkliste wird gerade von <strong>{{ checklist?.locked_by }}</strong> bearbeitet.
          Du kannst sie ansehen, aber nicht bearbeiten.
        </div>
      </v-alert>

      <!-- Error -->
      <v-alert v-if="fetchError" type="error" variant="tonal" class="ma-4">
        {{ fetchError }}
      </v-alert>

      <div v-if="checklist" class="pa-4" style="max-width: 800px; margin: 0 auto;">
        <!-- Description -->
        <p v-if="checklist.description" class="text-body-2 text-medium-emphasis mb-3">
          {{ checklist.description }}
        </p>

        <!-- Meta-Info Chips -->
        <div class="d-flex flex-wrap ga-2 mb-5">
          <v-chip
              v-for="user in checklist.assignees"
              :key="user"
              prepend-icon="mdi-account"
              size="small"
              variant="tonal"
          >
            {{ user }}
          </v-chip>
          <v-chip prepend-icon="mdi-calendar" size="small" variant="tonal">
            {{ formatDate(checklist.scheduled_start) || new Date().toLocaleDateString('de-DE') }}
          </v-chip>
          <v-chip v-if="checklist.asset_id" prepend-icon="mdi-factory" size="small" variant="tonal" color="primary">
            {{ checklist.asset_id }}
          </v-chip>
          <v-chip v-if="checklist.order_id" prepend-icon="mdi-clipboard-text" size="small" variant="tonal">
            Auftrag #{{ checklist.order_id }}
          </v-chip>
          <v-chip
              prepend-icon="mdi-circle"
              size="small"
              variant="tonal"
              :color="hasNio ? 'error' : allChecked ? 'success' : 'default'"
          >
            {{ isCompleted ? 'Abgeschlossen' : allChecked ? (hasNio ? 'Mängel festgestellt' : 'Alles i.O.') : 'In Bearbeitung' }}
          </v-chip>
        </div>

        <!-- Fortschritt -->
        <div class="d-flex align-center mb-5">
          <v-progress-linear
              :model-value="progress"
              :color="hasNio ? 'warning' : 'success'"
              height="10"
              rounded
              class="me-3"
              style="flex: 1"
          />
          <span class="text-body-2 font-weight-medium text-no-wrap">{{ checkedCount }}/{{ items.length }}</span>
        </div>

        <!-- Prüfpunkte -->
        <div class="checklist-items">
          <div v-for="(item, idx) in items" :key="idx" class="mb-3">
            <v-card
                :style="cardStyle(item)"
                :elevation="item.ok !== null ? 0 : 1"
                rounded="lg"
                class="checklist-card"
            >
              <!-- Farbiger linker Rand -->
              <div
                  class="checklist-indicator"
                  :style="{
                    backgroundColor: item.ok === true ? '#4CAF50' : item.ok === false ? '#F44336' : '#E0E0E0'
                  }"
              />

              <div class="pa-3 pa-md-4" style="margin-left: 6px">
                <!-- Obere Zeile: Nummer + Label + Buttons -->
                <div class="d-flex align-center">
                  <!-- Nummer -->
                  <div
                      class="checklist-number me-3"
                      :style="{
                        backgroundColor: item.ok === true ? '#4CAF50' : item.ok === false ? '#F44336' : '#9E9E9E',
                        color: 'white'
                      }"
                  >
                    <v-icon v-if="item.ok === true" icon="mdi-check" size="16"/>
                    <v-icon v-else-if="item.ok === false" icon="mdi-close" size="16"/>
                    <span v-else class="text-caption font-weight-bold">{{ idx + 1 }}</span>
                  </div>

                  <!-- Label -->
                  <span class="text-body-1 font-weight-medium flex-grow-1">{{ item.label }}</span>

                  <!-- Desktop Buttons -->
                  <div v-if="canEdit" class="d-none d-sm-flex ga-2 align-center">
                    <v-btn-toggle
                        :model-value="itemResult(item)"
                        @update:model-value="setResult(idx, $event)"
                        divided
                        density="compact"
                        variant="outlined"
                        rounded="pill"
                        style="height: 36px"
                    >
                      <v-btn
                          value="io"
                          :color="item.ok === true ? 'success' : undefined"
                          :variant="item.ok === true ? 'flat' : 'outlined'"
                          class="px-4"
                      >
                        <v-icon icon="mdi-check" size="18" class="me-1"/>
                        i.O.
                      </v-btn>
                      <v-btn
                          value="nio"
                          :color="item.ok === false ? 'error' : undefined"
                          :variant="item.ok === false ? 'flat' : 'outlined'"
                          class="px-4"
                      >
                        <v-icon icon="mdi-close" size="18" class="me-1"/>
                        n.i.O.
                      </v-btn>
                    </v-btn-toggle>

                    <v-btn
                        icon
                        size="small"
                        variant="text"
                        @click="item.showNote = !item.showNote"
                    >
                      <v-icon
                          :icon="item.note ? 'mdi-comment-text' : 'mdi-comment-plus-outline'"
                          :color="item.note ? 'primary' : undefined"
                          size="20"
                      />
                    </v-btn>
                  </div>

                  <!-- Readonly indicator -->
                  <div v-if="!canEdit" class="d-none d-sm-flex">
                    <v-chip
                        size="small"
                        :color="item.ok === true ? 'success' : item.ok === false ? 'error' : 'grey'"
                        variant="flat"
                    >
                      {{ item.ok === true ? 'i.O.' : item.ok === false ? 'n.i.O.' : 'Offen' }}
                    </v-chip>
                  </div>
                </div>

                <!-- Mobile Buttons -->
                <div v-if="canEdit" class="d-flex d-sm-none ga-2 mt-3">
                  <v-btn
                      :variant="item.ok === true ? 'flat' : 'outlined'"
                      :color="item.ok === true ? 'success' : undefined"
                      size="default"
                      class="flex-grow-1"
                      @click="setResult(idx, 'io')"
                  >
                    <v-icon icon="mdi-check" class="me-1"/>
                    i.O.
                  </v-btn>
                  <v-btn
                      :variant="item.ok === false ? 'flat' : 'outlined'"
                      :color="item.ok === false ? 'error' : undefined"
                      size="default"
                      class="flex-grow-1"
                      @click="setResult(idx, 'nio')"
                  >
                    <v-icon icon="mdi-close" class="me-1"/>
                    n.i.O.
                  </v-btn>
                  <v-btn
                      icon
                      variant="text"
                      @click="item.showNote = !item.showNote"
                  >
                    <v-icon
                        :icon="item.note ? 'mdi-comment-text' : 'mdi-comment-plus-outline'"
                        :color="item.note ? 'primary' : undefined"
                    />
                  </v-btn>
                </div>

                <!-- Mobile Readonly -->
                <div v-if="!canEdit" class="d-flex d-sm-none mt-2">
                  <v-chip
                      size="small"
                      :color="item.ok === true ? 'success' : item.ok === false ? 'error' : 'grey'"
                      variant="flat"
                  >
                    {{ item.ok === true ? 'i.O.' : item.ok === false ? 'n.i.O.' : 'Offen' }}
                  </v-chip>
                </div>

                <!-- Bemerkung -->
                <v-expand-transition>
                  <div v-if="item.showNote || item.ok === false" class="mt-3">
                    <v-textarea
                        v-model="item.note"
                        :label="item.ok === false ? 'Bemerkung (Pflicht bei n.i.O.)' : 'Bemerkung (optional)'"
                        :color="item.ok === false ? 'error' : 'primary'"
                        :readonly="!canEdit"
                        :variant="useGlobalSetting().globalTextVariant"
                        density="compact"
                        rows="2"
                        auto-grow
                        hide-details
                        rounded="lg"
                    />
                  </div>
                </v-expand-transition>

                <!-- Bearbeitet von -->
                <div v-if="item.checked_by" class="mt-1 text-caption text-medium-emphasis">
                  <v-icon icon="mdi-account-edit" size="12" class="me-1"/>
                  {{ item.checked_by }}
                  <span v-if="item.checked_at"> &middot; {{ formatDateTime(item.checked_at) }}</span>
                </div>
              </div>
            </v-card>
          </div>
        </div>

        <!-- Zusammenfassung bei n.i.O. -->
        <v-expand-transition>
          <v-alert
              v-if="hasNio"
              type="warning"
              variant="tonal"
              class="mb-4"
              prominent
          >
            <div class="font-weight-bold mb-1">{{ nioCount }} Mängel festgestellt</div>
            <div v-for="item in nioItems" :key="item.label" class="text-body-2">
              &bull; {{ item.label }}{{ item.note ? ': ' + item.note : '' }}
            </div>
          </v-alert>
        </v-expand-transition>

        <!-- Already completed info -->
        <v-alert
            v-if="isCompleted"
            type="success"
            variant="tonal"
            class="mb-4"
            prominent
        >
          <div class="font-weight-bold">Abgeschlossen</div>
          <div class="text-body-2">
            von {{ checklist.completed_by }}
            <span v-if="checklist.completed_at"> am {{ formatDate(checklist.completed_at) }}</span>
          </div>
        </v-alert>

        <p v-if="canEdit && !allChecked" class="text-caption text-medium-emphasis mt-2 text-center">
          Alle Prüfpunkte müssen bewertet werden
        </p>
        <p v-else-if="canEdit && nioMissingNote" class="text-caption text-error mt-2 text-center">
          Alle n.i.O. Punkte benötigen eine Bemerkung
        </p>
      </div>
    </v-sheet>

    <!-- Desktop: Buttons unten -->
    <v-divider v-if="!useGlobalSetting().isMobile"/>

    <v-card-actions v-if="!useGlobalSetting().isMobile" class="bg-surface-light">
      <v-btn variant="plain" @click="onClose">
        <v-icon color="red" icon="mdi-cancel" start/>
        Schließen
      </v-btn>
      <v-spacer/>

      <!-- Zwischenspeichern -->
      <v-btn
          v-if="canEdit && hasUnsavedChanges"
          variant="plain"
          :loading="saving"
          @click="saveProgress"
      >
        <v-icon color="primary" icon="mdi-content-save-outline" start/>
        Zwischenspeichern
      </v-btn>

      <!-- Abschließen -->
      <v-btn
          v-if="canEdit"
          variant="plain"
          :disabled="!allChecked || (hasNio && nioMissingNote) || saving"
          :loading="saving"
          @click="completeChecklist"
      >
        <v-icon :color="hasNio ? 'warning' : 'success'" icon="mdi-check-all" start/>
        {{ hasNio ? 'Mit Mängeln abschließen' : 'Abschließen' }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, reactive, ref} from 'vue'
import {supabase} from '@/api'
import {getCurrentUser, useGlobalSetting, useSecondPopup} from '@/lib/utils'
import {acquireChecklistLock, releaseChecklistLock} from '@/api/ckecklist'
import {useAuth} from '@/lib/useAuth'
import ChecklistAssignForm from '@/components/checklists/ChecklistAssignForm.vue'
import type {Checklist} from '@/types/Checklist'

interface ChecklistItem {
  label: string
  ok: boolean | null
  note: string | null
  showNote: boolean
  checked_by: string | null
  checked_at: string | null
}

const props = defineProps<{
  checklistId: number
}>()

const emit = defineEmits<{
  close: []
  completed: [checklist: Checklist]
  saved: [checklist: Checklist]
}>()

const global = useGlobalSetting()
const saving = ref(false)
const fetchError = ref<string | null>(null)
const checklist = ref<Checklist | null>(null)
const items = reactive<ChecklistItem[]>([])
const hasUnsavedChanges = ref(false)
const hasLock = ref(false)
let lockRefreshInterval: ReturnType<typeof setInterval> | null = null

// Computed
const isCompleted = computed(() =>
    checklist.value?.state === 'BEEND' || checklist.value?.state === 'GESCHL'
)

const lockedByOther = computed(() =>
    checklist.value?.locked_by
    && checklist.value.locked_by !== getCurrentUser()
    && !hasLock.value
)

const canEdit = computed(() =>
    !isCompleted.value && !lockedByOther.value && hasLock.value
)

const isAdminOrMod = computed(() => ['admin', 'mod'].includes(useAuth().getRole()))

const checkedCount = computed(() => items.filter(i => i.ok !== null).length)
const progress = computed(() => items.length > 0 ? (checkedCount.value / items.length) * 100 : 0)
const allChecked = computed(() => items.length > 0 && checkedCount.value === items.length)
const hasNio = computed(() => items.some(i => i.ok === false))
const nioCount = computed(() => items.filter(i => i.ok === false).length)
const nioItems = computed(() => items.filter(i => i.ok === false))
const nioMissingNote = computed(() => items.some(i => i.ok === false && !i.note?.trim()))

// Helpers
function itemResult(item: ChecklistItem): string | null {
  if (item.ok === true) return 'io'
  if (item.ok === false) return 'nio'
  return null
}

function setResult(idx: number, value: string | null) {
  if (!value || !canEdit.value) return
  const item = items[idx]
  const newOk = value === 'io' ? true : value === 'nio' ? false : null

  if (item.ok === newOk) {
    item.ok = null
    item.checked_by = null
    item.checked_at = null
  } else {
    item.ok = newOk
    item.checked_by = getCurrentUser()
    item.checked_at = new Date().toISOString()
  }
  hasUnsavedChanges.value = true
}

function cardStyle(item: ChecklistItem) {
  if (item.ok === true) return {border: '2px solid #4CAF50', backgroundColor: '#E8F5E9'}
  if (item.ok === false) return {border: '2px solid #F44336', backgroundColor: '#FFEBEE'}
  return {border: '2px solid transparent'}
}

function formatDate(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null
  try {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  } catch {
    return dateStr
  }
}

function formatDateTime(dateStr: string | null): string | null {
  if (!dateStr) return null
  try {
    return new Date(dateStr).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
  } catch {
    return dateStr
  }
}

function getItemsPayload() {
  return items.map(({label, ok, note, checked_by, checked_at}) => ({
    label, ok, note: note || null, checked_by: checked_by || null, checked_at: checked_at || null
  }))
}

// Locking
async function tryAcquireLock() {
  if (isCompleted.value) return

  const success = await acquireChecklistLock(props.checklistId, getCurrentUser())
  hasLock.value = success

  if (success) {
    lockRefreshInterval = setInterval(async () => {
      await acquireChecklistLock(props.checklistId, getCurrentUser())
    }, 2 * 60 * 1000)
  }
}

async function releaseLock() {
  if (hasLock.value) {
    if (lockRefreshInterval) {
      clearInterval(lockRefreshInterval)
      lockRefreshInterval = null
    }
    await releaseChecklistLock(props.checklistId)
    hasLock.value = false
  }
}

async function onClose() {
  await releaseLock()
  emit('close')
}

// Data fetching
async function fetchChecklist() {
  global.loadingMessage = 'Checkliste wird geladen...'
  global.isLoading = true
  fetchError.value = null

  const {data, error} = await supabase
      .from('checklists')
      .select('*')
      .eq('id', props.checklistId)
      .single()

  if (error || !data) {
    fetchError.value = error?.message || 'Checkliste nicht gefunden'
    global.isLoading = false
    return
  }

  checklist.value = data as Checklist

  items.length = 0
  const dbItems = (data.items as any[]) || []
  for (const item of dbItems) {
    items.push({
      label: item.label || '',
      ok: item.ok ?? null,
      note: item.note ?? null,
      showNote: !!(item.note) || item.ok === false,
      checked_by: item.checked_by ?? null,
      checked_at: item.checked_at ?? null
    })
  }

  hasUnsavedChanges.value = false
  global.isLoading = false

  await tryAcquireLock()
}

// Save progress
async function saveProgress() {
  if (!checklist.value || !canEdit.value) return

  saving.value = true

  const {error} = await supabase
      .from('checklists')
      .update({
        items: getItemsPayload(),
        state: 'INBEARB'
      })
      .eq('id', checklist.value.id)

  if (error) {
    console.error('Save failed:', error)
  } else {
    hasUnsavedChanges.value = false
    if (checklist.value) {
      checklist.value.state = 'INBEARB'
      checklist.value.items = getItemsPayload()
    }
    emit('saved', checklist.value!)
  }

  saving.value = false
}

// Complete checklist
async function completeChecklist() {
  if (!checklist.value || !allChecked.value || !canEdit.value) return
  if (hasNio.value && nioMissingNote.value) return

  saving.value = true

  const now = new Date().toISOString()
  const {error} = await supabase
      .from('checklists')
      .update({
        items: getItemsPayload(),
        state: 'BEEND',
        completed_by: getCurrentUser(),
        completed_at: now,
        locked_by: null,
        locked_at: null
      })
      .eq('id', checklist.value.id)

  if (error) {
    console.error('Complete failed:', error)
  } else {
    if (checklist.value) {
      checklist.value.state = 'BEEND'
      checklist.value.completed_by = getCurrentUser()
      checklist.value.completed_at = now
      checklist.value.locked_by = null
      checklist.value.locked_at = null
      checklist.value.items = getItemsPayload()
    }
    hasUnsavedChanges.value = false
    hasLock.value = false
    if (lockRefreshInterval) {
      clearInterval(lockRefreshInterval)
      lockRefreshInterval = null
    }
    emit('completed', checklist.value!)
  }

  saving.value = false
}

function openEditDialog() {
  if (!checklist.value) return

  useSecondPopup().open(
      ChecklistAssignForm,
      {
        checklistId: checklist.value.id,
        checklistTitle: checklist.value.title,
        initialAssignees: checklist.value.assignees,
        initialAssetId: checklist.value.asset_id,
        initialOrderId: checklist.value.order_id,
        initialScheduledStart: checklist.value.scheduled_start,
        initialScheduledEnd: checklist.value.scheduled_end,
        onSaved: () => fetchChecklist(),
        onCancel: () => useSecondPopup().close()
      },
      {
        contentFilled: true,
        maxWidth: 600,
        persistent: false,
      }
  )
}

onMounted(fetchChecklist)

onBeforeUnmount(async () => {
  await releaseLock()
})
</script>

<style scoped>
.checklist-card {
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;
}

.checklist-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.checklist-indicator {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  transition: background-color 0.3s ease;
}

.checklist-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  transition: background-color 0.3s ease;
}
</style>
